/**
 * Game registry — tracks which games are installed and in which vault.
 *
 * Backed by localStorage so it's readable before any IndexedDB opens.
 * Each game type maps to at most one vault (IDB database). The legacy
 * vault `f2ce-shell` is preserved as-is for existing users; new installs
 * go to `f2ce-fo1` or `f2ce-fo2`.
 */

import type { GameType } from './config';

const STORAGE_KEY = 'f2ce-games';
const ACTIVE_KEY = 'f2ce-active';

export interface GameEntry {
  gameType: GameType;
  dbName: string;
  installedAt: number;
  totalBytes: number;
  fileCount: number;
  hasRpu?: boolean;
  rpuVersion?: string;
}

export function readRegistry(): GameEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeRegistry(entries: GameEntry[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
}

export function registerGame(entry: GameEntry): void {
  const reg = readRegistry().filter((e) => e.gameType !== entry.gameType);
  reg.push(entry);
  reg.sort((a, b) => (a.gameType < b.gameType ? -1 : 1));
  writeRegistry(reg);
}

export function unregisterGame(gameType: GameType): void {
  writeRegistry(readRegistry().filter((e) => e.gameType !== gameType));
  if (getActiveGame() === gameType) {
    localStorage.removeItem(ACTIVE_KEY);
  }
}

export function vaultDbName(game: GameType): string {
  const entry = readRegistry().find((e) => e.gameType === game);
  return entry?.dbName ?? `f2ce-${game}`;
}

export function getActiveGame(): GameType | null {
  // Read defensively: this is now on the path of every save-archive and
  // game-data read (see config.ts `activeGameType`), and Safari in private mode
  // throws on localStorage access rather than returning null.
  try {
    return (localStorage.getItem(ACTIVE_KEY) as GameType) || null;
  } catch {
    return null;
  }
}

export function setActiveGame(game: GameType): void {
  localStorage.setItem(ACTIVE_KEY, game);
}

export function clearRegistry(): void {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(ACTIVE_KEY);
}
